import React, { useMemo, useState } from "react";
import api from "../lib/api";
import { Card, Button, Input, Table, Th, Td } from "./ui";
import { errMsg, fmtDate } from "../lib/utils";
import { toast } from "sonner";
import { X, Upload, Eye, ArrowLeft, Save, FileSpreadsheet, Cpu, AlertTriangle } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export function periodRange(month, cutoff) {
  const [y, m] = month.split("-").map(Number);
  if (cutoff > 0) return [iso(new Date(y, m - 2, cutoff + 1)), iso(new Date(y, m - 1, cutoff))];
  return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
}

function FileBox({ label, testid, multiple, files, onChange, hint }) {
  return (
    <label className="block border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-blue-400 cursor-pointer" data-testid={`${testid}-box`}>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      <input type="file" accept=".dat,.txt" multiple={multiple} data-testid={testid} className="text-xs w-full"
        onChange={(e) => onChange(Array.from(e.target.files || []))}/>
      <div className="text-[11px] text-slate-500 mt-1">{files.length ? files.map((f) => f.name).join(", ") : hint}</div>
    </label>
  );
}

const TONE = { slate: "bg-slate-50 border-slate-200", emerald: "bg-emerald-50 border-emerald-200", amber: "bg-amber-50 border-amber-200", orange: "bg-orange-50 border-orange-200", rose: "bg-rose-50 border-rose-200" };
function Stat({ label, value, tone = "slate" }) {
  return (
    <div className={`p-3 rounded border ${TONE[tone]}`}>
      <div className="text-[10px] uppercase font-bold text-slate-500">{label}</div>
      <div className="font-heading font-black text-xl">{value}</div>
    </div>
  );
}

function MiniTable({ title, headers, rows, testid }) {
  if (!rows.length) return null;
  return (
    <div className="border border-rose-200 rounded overflow-hidden mb-3" data-testid={testid}>
      <div className="px-3 py-2 bg-rose-50 text-[11px] uppercase font-bold text-rose-800">{title} ({rows.length})</div>
      <table className="w-full text-xs">
        <thead><tr className="bg-rose-50/50">{headers.map((h) => <th key={h} className="text-left px-3 py-1.5 font-bold text-slate-600">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-rose-100">{r.map((c, j) => <td key={j} className="px-3 py-1.5">{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export default function ZkImportModal({ month, cutoff, onClose, onSaved }) {
  const [r0, r1] = periodRange(month, cutoff);
  const [logs, setLogs] = useState([]);
  const [userFile, setUserFile] = useState([]);
  const [from, setFrom] = useState(r0);
  const [to, setTo] = useState(r1);
  const [mode, setMode] = useState("merge");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("rekap");
  const [detailEmp, setDetailEmp] = useState(null);

  const formData = (extra = {}) => {
    const fd = new FormData();
    logs.forEach((f) => fd.append("log_files", f));
    if (userFile[0]) fd.append("user_file", userFile[0]);
    fd.append("date_from", from); fd.append("date_to", to); fd.append("mode", mode);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    return fd;
  };
  const run = async (commit) => {
    if (!logs.length) return toast.error("Upload minimal 1 file log (.dat)");
    setBusy(true);
    try {
      const { data } = await api.post("/hr/attendance/zk-import", formData({ commit }), { headers: { "Content-Type": "multipart/form-data" } });
      if (commit) { toast.success(`${data.saved} hari absensi tersimpan (${mode === "merge" ? "Merge" : "Timpa Total"}) — lembur tidak diubah`); onSaved(); return; }
      setPreview(data); setTab("rekap"); setDetailEmp(data.employees[0]?.employee_id || null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = () => {
    if (!window.confirm(`Simpan ${preview.totals.hadir} hari absensi (${preview.employees.length} karyawan) periode ${fmtDate(from)} – ${fmtDate(to)}?\nMode: ${mode === "merge" ? "Merge (data lama dipertahankan)" : "Timpa Total"}. Lembur tidak disentuh.`)) return;
    run(true);
  };
  const exportXls = async () => {
    setBusy(true);
    try {
      const res = await api.post("/hr/attendance/zk-import/export", formData(), { responseType: "blob", headers: { "Content-Type": "multipart/form-data" } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `PREVIEW_Rekap_Absensi_${from}_${to}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const emp = useMemo(() => preview?.employees.find((e) => e.employee_id === detailEmp), [preview, detailEmp]);
  const problems = preview ? preview.employees.filter((e) => e.no_in || e.no_out) : [];

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-3" data-testid="zk-import-modal">
      <Card className={`w-full ${preview ? "max-w-[96vw] h-[94vh]" : "max-w-2xl"} p-6 overflow-y-auto`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-xl inline-flex items-center gap-2"><Cpu size={18} className="text-blue-700"/> Import dari Mesin (.dat){preview && " — Preview"}</h3>
            <p className="text-xs text-slate-500">{preview ? "Belum ada data yang disimpan. Cek dulu, lalu Simpan ke Absensi." : "File attlog (bulan lalu + bulan ini) dan user.dat dari mesin ZKTeco. Lembur tidak diimport."}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="zk-close"><X size={18}/></button>
        </div>

        {!preview && (
          <div className="space-y-3">
            <FileBox label="File log (bisa lebih dari satu: bulan lalu + bulan ini)" testid="zk-log-files" multiple files={logs} onChange={setLogs} hint="mis. BZD…_attlog sep.dat, BZD…_attlog okt.dat"/>
            <FileBox label="File user (user.dat)" testid="zk-user-file" files={userFile} onChange={setUserFile} hint="Untuk laporan nama & ID di mesin (opsional)"/>
            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-[11px] uppercase font-bold text-slate-500 mb-1">Dari tanggal</div><Input type="date" data-testid="zk-from" value={from} onChange={(e) => setFrom(e.target.value)}/></div>
              <div><div className="text-[11px] uppercase font-bold text-slate-500 mb-1">Sampai tanggal</div><Input type="date" data-testid="zk-to" value={to} onChange={(e) => setTo(e.target.value)}/></div>
            </div>
            <p className="text-[11px] text-slate-500">Terisi otomatis dari periode terpilih{cutoff > 0 ? ` (cutoff tgl ${cutoff})` : ""}. Hari dinilai dari tanggal scan masuk — pulang lewat tengah malam tetap ikut hari masuknya.</p>
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="secondary" onClick={onClose}>Batal</Button>
              <Button data-testid="zk-preview-btn" disabled={busy} onClick={() => run(false)}><Eye size={14}/> {busy ? "Memproses..." : "Preview"}</Button>
            </div>
          </div>
        )}

        {preview && (
          <>
            {preview.uncovered_dates.length > 0 && (
              <div className="mb-3 p-3 rounded bg-amber-50 border border-amber-300 text-xs text-amber-900 flex gap-2" data-testid="zk-uncovered-warning">
                <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
                <div>Rentang tidak tertutup data file (file berisi {fmtDate(preview.file_range[0])} – {fmtDate(preview.file_range[1])}). Tanggal kosong: {preview.uncovered_dates.map((d) => fmtDate(d)).join(", ")}</div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4" data-testid="zk-summary">
              <Stat label="Rentang" value={<span className="text-sm">{fmtDate(from)} – {fmtDate(to)}</span>}/>
              <Stat label="Karyawan" value={preview.totals.employees}/>
              <Stat label="Total hari hadir" value={preview.totals.hadir} tone="emerald"/>
              <Stat label="Telat" value={preview.totals.telat} tone="amber"/>
              <Stat label="Scan bermasalah" value={preview.totals.problem_scans} tone="orange"/>
              <Stat label="ID tidak cocok" value={preview.totals.unmatched} tone="rose"/>
            </div>
            <div className="flex gap-1 mb-3 border-b">
              {[["rekap", "Rekap"], ["detail", "Detail Harian"], ["mismatch", "Tidak Cocok"], ["conflict", `Konflik (${preview.conflicts.length})`]].map(([k, v]) => (
                <button key={k} data-testid={`zk-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${tab === k ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{v}</button>
              ))}
            </div>

            {tab === "rekap" && (
              <Table>
                <thead><tr><Th>ID</Th><Th>Nama</Th><Th className="text-right">Hadir</Th><Th className="text-right">Telat</Th><Th className="text-right">Kurang Jam</Th><Th className="text-right">Tdk Scan Masuk</Th><Th className="text-right">Tdk Scan Pulang</Th><Th className="text-right">Total Jam</Th></tr></thead>
                <tbody>{preview.employees.map((e) => (
                  <tr key={e.employee_id} data-testid={`zk-rekap-${e.employee_no}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => { setDetailEmp(e.employee_id); setTab("detail"); }}>
                    <Td className="font-mono">{e.employee_no}</Td><Td className="font-bold text-blue-800 underline decoration-dotted">{e.employee_name}</Td>
                    <Td className="text-right">{e.hadir}</Td><Td className={`text-right ${e.telat ? "text-amber-700 font-bold" : ""}`}>{e.telat}</Td>
                    <Td className="text-right">{e.kurang_jam}</Td><Td className={`text-right ${e.no_in ? "text-orange-700 font-bold" : ""}`}>{e.no_in}</Td>
                    <Td className={`text-right ${e.no_out ? "text-orange-700 font-bold" : ""}`}>{e.no_out}</Td><Td className="text-right font-mono">{e.worked_hours}</Td>
                  </tr>))}</tbody>
              </Table>
            )}

            {tab === "detail" && (
              <div className="flex gap-4">
                <div className="w-56 shrink-0 border rounded divide-y max-h-[60vh] overflow-y-auto">
                  {preview.employees.map((e) => (
                    <button key={e.employee_id} onClick={() => setDetailEmp(e.employee_id)} data-testid={`zk-detail-emp-${e.employee_no}`}
                      className={`block w-full text-left px-3 py-2 text-xs ${detailEmp === e.employee_id ? "bg-blue-50 font-bold text-blue-800" : "hover:bg-slate-50"}`}>{e.employee_no} · {e.employee_name}</button>
                  ))}
                </div>
                <div className="flex-1">
                  {emp && (
                    <Table>
                      <thead><tr><Th>Tanggal</Th><Th>Masuk</Th><Th>Pulang</Th><Th>Status</Th><Th className="text-right">Jam Kerja</Th><Th>Flag</Th></tr></thead>
                      <tbody>{emp.records.map((r) => (
                        <tr key={r.date} data-testid={`zk-day-${r.date}`}>
                          <Td>{fmtDate(r.date)} <span className="text-[10px] text-slate-400 uppercase">{r.day_of_week}</span>{r.is_holiday && <span className="ml-1 text-[10px] text-rose-600">★ {r.holiday_name}</span>}</Td>
                          <Td className="font-mono">{r.check_in || "-"}</Td>
                          <Td className="font-mono">{r.check_out || "-"}{r.check_out_next_day && <span className="ml-1 text-[10px] font-bold text-indigo-700">(+1)</span>}</Td>
                          <Td>{r.status}</Td>
                          <Td className="text-right font-mono">{(r.worked_minutes / 60).toFixed(1)}</Td>
                          <Td className="text-xs">
                            {r.is_late && <span className="mr-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold text-[10px]">TELAT {r.late_minutes}m</span>}
                            {r.is_under_hours && <span className="mr-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-bold text-[10px]">KURANG JAM</span>}
                            {r.flags.map((f) => <span key={f} className="mr-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 font-bold text-[10px]">{f}</span>)}
                          </Td>
                        </tr>))}</tbody>
                    </Table>
                  )}
                </div>
              </div>
            )}

            {tab === "mismatch" && (
              <div data-testid="zk-mismatch">
                <MiniTable testid="zk-unmatched" title="ID ada di mesin, tidak ada di sistem — tidak diimport" headers={["ID", "Nama di mesin", "Jumlah scan"]} rows={preview.unmatched.map((u) => [u.pin, u.machine_name || "-", u.scan_count])}/>
                <MiniTable testid="zk-not-in-user" title="ID ada di log, tidak ada di file user" headers={["ID", "Jumlah scan"]} rows={preview.not_in_userfile.map((u) => [u.pin, u.scan_count])}/>
                <MiniTable testid="zk-no-scan" title="Karyawan di sistem tanpa scan dalam rentang" headers={["ID Karyawan", "Nama"]} rows={preview.no_scan_employees.map((e) => [e.employee_no || "(kosong)", e.employee_name])}/>
                <MiniTable testid="zk-name-warn" title="ID sama, nama jauh berbeda — tetap diimport" headers={["ID", "Nama di mesin", "Nama di sistem"]} rows={preview.name_warnings.map((w) => [w.pin, w.machine_name, w.employee_name])}/>
                <MiniTable testid="zk-problems" title="Scan bermasalah per karyawan" headers={["ID", "Nama", "Tidak scan masuk", "Tidak scan pulang"]} rows={problems.map((e) => [e.employee_no, e.employee_name, e.no_in, e.no_out])}/>
              </div>
            )}

            {tab === "conflict" && (
              preview.conflicts.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">Tidak ada konflik dengan data lama.</div> : (
                <Table>
                  <thead><tr><Th>Karyawan</Th><Th>Tanggal</Th><Th>Field</Th><Th>Data lama</Th><Th>Jadi</Th></tr></thead>
                  <tbody>{preview.conflicts.map((c, i) => (
                    <tr key={i}><Td>{c.employee_name}</Td><Td>{fmtDate(c.date)}</Td><Td>{c.field}</Td><Td className="font-mono text-rose-700">{String(c.old)}</Td><Td className="font-mono text-emerald-700">{String(c.new)}</Td></tr>))}</tbody>
                </Table>
              )
            )}

            <div className="flex flex-wrap items-center gap-2 pt-4 mt-4 border-t sticky bottom-0 bg-white">
              <Button variant="secondary" data-testid="zk-back-btn" onClick={() => setPreview(null)}><ArrowLeft size={14}/> Kembali</Button>
              <Button variant="secondary" data-testid="zk-export-btn" disabled={busy} onClick={exportXls}><FileSpreadsheet size={14}/> Export Excel</Button>
              <div className="ml-auto flex items-center gap-2 text-xs">
                <span className="font-bold text-slate-500 uppercase">Mode</span>
                {[["merge", "Merge (aman)"], ["overwrite", "Timpa Total"]].map(([k, v]) => (
                  <label key={k} className="inline-flex items-center gap-1 cursor-pointer"><input type="radio" data-testid={`zk-mode-${k}`} checked={mode === k} onChange={() => setMode(k)}/> {v}</label>
                ))}
                <Button variant="secondary" onClick={onClose}>Batal</Button>
                <Button data-testid="zk-save-btn" disabled={busy || !preview.totals.hadir} onClick={save}><Save size={14}/> {busy ? "Menyimpan..." : "Simpan ke Absensi"}</Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
